import { useNavigate, unstable_useBlocker as useBlocker } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store';
import { authActions, authSelector } from '@/store/reducers/auth';
import { gameActions, gameSelector } from '@/store/reducers/game';
import commonUtils from '@/utils/common';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import UseQuizTimer from '@/hooks/UseQuizTimer';
import usePageVisibility from '@/hooks/UsePageVisibility';

import {
  GameStatus,
  GameTypes,
  HintsType,
  NewGameTypes,
  PopUpFocusProps,
  PopUpHintsProps,
  PopUpKeepGoingProps,
  PopUpPauseProps,
  PopUpSummaryProps,
  PopUpTimeIsUpProps,
  PopUpTypes,
  PopUpWordsListProps,
  QuizQuestionResult,
} from '@/types/game';
import GameUtils from '@/utils/gameUtils';
import UseTopicQuizzes from '@/hooks/UseTopicQuizzes';
import { SkillTypes } from '@/types/skill';
import { levelTestActions, levelTestSelector } from '@/store/reducers/level-test';
import { Quiz } from '@/types/quiz';
import { Question } from '@/types/question';
import { Block } from '@/types/game';
import { appSettingsActions, appSettingsSelector } from '@/store/reducers/settings';
import questionsThunk from '@/store/thunks/questions';
import quizzesThunks from '@/store/thunks/quizzes';
import UseInitialLevelTestGame from '@/hooks/UseInitialLevelTest';
import { TopicTypes, Topic } from '@/types/topic';
import { SkillSettingsItem } from '@/types/user';
import { unitActions, unitSelector } from '@/store/reducers/unit';
import { resolveProgressUnitId } from '@/store/units';
import UserUtils from '@/utils/user';
import { progressService } from '@/services';
import UseScreenSize from '@/hooks/UseScreenSize';
import {
  activityLocationsActions,
  activityLocationsSelector,
} from '@/store/reducers/activityLocations';
import { useDrawer } from '@/context/DrawerContext';

export interface UseQuizInterface {
  loadError: boolean;
  savingQuizProgressData: boolean;
  loadQuizProgress: boolean;
  currentQuestion: any;
  selectedType: SkillTypes | null;
  selectedTopic: Topic | null;
  selectedSkill: SkillSettingsItem | null | undefined;
  selectedQuiz: Quiz | null;
  gameStatus: GameStatus;
  timesUpShown: boolean;
  focusShown: boolean;
  correctAnswers: number;
  inCorrectAnswers: number;
  currentQuestionIndex: number;
  gameQuestions: Question[];
  gameTypeReal: GameTypes | NewGameTypes | null;
  coins: number;
  selectedHint: HintsType | null;
  pickedAnswer: string | null;
  currentTime: number;
  shouldDisplayPopUp: boolean;
  popUpType: PopUpTypes | null;
  popUpProps:
    | PopUpKeepGoingProps
    | PopUpFocusProps
    | PopUpTimeIsUpProps
    | PopUpPauseProps
    | PopUpSummaryProps
    | PopUpHintsProps
    | PopUpWordsListProps
    | null;
  isMuted: boolean;
  currentCoins: number | undefined;
  allowRetry: boolean;
  showPreQuiz: boolean;
  showAnswerInOptions: boolean;
  onClosePopUp: () => Promise<void>;
  onPause: () => Promise<void>;
  onMute: () => void;
  onStartQuiz: () => void;
  onPractice: () => void;
  onExit: () => Promise<void>;
  onDisableRetry: () => Promise<void>;
  onAknowledge: (id: number, isKnown: boolean) => void;
  onSwipe: (questionId: number) => void;
  goToNextQuestion: (force?: boolean) => void;
  toggleCheckAnswer: (answer: string) => Promise<void>;
  currentReadQuestionIndex: number;
  setCurrentQuestionTempData: (data: any, key?: string) => void;
  currentQuestionTempData: any;
}

const UseQuiz = (): UseQuizInterface => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(authSelector);
  const isTeacher = UserUtils.isTeacher(user);
  const { isMobile } = UseScreenSize();
  const drawer = useDrawer();
  const {
    selectedUnit,
    topics,
    selectedTopic: selectedTopicNew,
    units,
    selectedLevel,
  } = useAppSelector(unitSelector);
  const { byActivityId: activityLocationsById } = useAppSelector(activityLocationsSelector);
  const {
    selectedType,
    selectedTopic,
    selectedSkill,
    selectedQuiz,
    gameStatus,
    timesUpShown,
    focusShown,
    keepGoingShown,
    correctAnswers,
    total_correctAnswers,
    inCorrectAnswers,
    currentQuestionIndex,
    currentReadQuestionIndex,
    gameQuestions,
    coins,
    selectedHint,
    pickedAnswer,
    currentTime,
    isMuted,
    allowRetry,
    gameType: gameTypeReal,
    showPreQuiz,
    showAnswerInOptions,
    quizQuestionResultData,
    total_inCorrectAnswers,
    visitFocus,
    visitKeepGoing,
    hintsData,
    totalQuizTime,
    skipSpeechRecordingQuestions,
    isOpenWritingAnswerInEnglish,
    pauseTimer,
    hasScore: gameHasScore,
  } = useAppSelector(gameSelector);
  //FIXME: NEED TO REPLACE WITH CORRECT GAMETYPE
  const gameType = gameQuestions[0]?.gameType as any;

  const { questions, quizzes } = useAppSelector(appSettingsSelector);

  const {
    quizzes: levelTestQuizzes,
    currentQuizIndex: levelTestQuizIndex,
    currentOverAllQuizNumber,
    correctAnswers: levelTestCorrectAnswers,
    activity,
    questionIndex,
  } = useAppSelector(levelTestSelector);

  const { startInitialLevelTestGame } = UseInitialLevelTestGame();

  UseQuizTimer();
  const { isVisible: isPageVisible } = usePageVisibility();
  const pausedByTabSwitchRef = useRef<boolean>(false);

  const isMountedRef = useRef(true);
  const gameStatusRef = useRef(gameStatus);
  gameStatusRef.current = gameStatus;

  const { sortedQuizzes } = UseTopicQuizzes();

  const navigate = useNavigate();
  const [savingQuizProgressData, setSavingQuizProgressData] = useState(false);
  const [loadQuizProgress, setLoadQuizProgress] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [shouldDisplayPopUp, setShouldDisplayPopUp] = useState(false);
  const [popUpType, setPopUpType] = useState<PopUpTypes | null>(null);
  const [popUpProps, setPopUpProps] = useState<
    | PopUpKeepGoingProps
    | PopUpFocusProps
    | PopUpTimeIsUpProps
    | PopUpPauseProps
    | PopUpSummaryProps
    | PopUpHintsProps
    | PopUpWordsListProps
    | null
  >(null);

  const [currentQuestionTempData, setCurrentQuestionTempDataState] = useState<any>({});
  const currentQuestionTempDataRef = useRef<any>({});

  const currentCoins = user?.progress?.coins;

  const { isLevelTest, isFinalAssessment } = useAppSelector(gameSelector);

  const isInEndOfSkillTest = selectedTopic?.topicType === TopicTypes.EndOfSkillTest;

  const isRead = gameType === NewGameTypes.Read;
  const isMemory = gameType === NewGameTypes.Memory;

  const ignoreFocusTypes = [
    GameTypes.ClosedSpelling,
    GameTypes.Pronunciation,
    NewGameTypes.NaturalConversation,
  ];

  //if the current quiz number is 10, 20, 30, show keep going modal
  const levelTestShowKeepGoing = [10, 20, 30].includes(currentOverAllQuizNumber);

  //Check the current quiz number and show keep going modal
  useEffect(() => {
    if (levelTestShowKeepGoing) {
      showKeepGoing();
    }
  }, [currentOverAllQuizNumber]);

  const getNextTopic = () => {
    const currentTopics = topics[selectedUnit?.id || 1];

    const currentTopic = currentTopics.find(topic => topic.id === selectedTopic?.id);

    if (!currentTopic) return null;

    if (currentTopic?.order >= currentTopics.length) {
      return null;
    }

    const nextTopic = currentTopics.find(topic => topic.order === currentTopic.order + 1);

    //FIXME: Temporary solution for setSelectedTopic structure
    const mockNextTopic = {
      id: nextTopic?.id || 0,
      nameLocal: null,
      name: nextTopic?.name,
      order: nextTopic?.order,
      quizzesCount: 1,
      topicType: 1,
      activitiesInTopic: nextTopic?.activitiesInTopic,
      userData: {
        receivedStars: 8,
        quizzesCompleted: 3,
      },
    };

    //FIXME: Need to use "nextTopic" instead
    return { oldData: mockNextTopic, newData: nextTopic };
  };

  const getNextTopicQuiz = async () => {
    const currentQuizIndex = sortedQuizzes.findIndex(quiz => quiz.id === selectedQuiz?.id);

    const nextQuiz = sortedQuizzes[currentQuizIndex + 1];
    return { topic: selectedTopic, quiz: nextQuiz, newTopic: selectedTopicNew };
  };

  const showFocusModal = () => {
    dispatch(gameActions.setFocusShown());
    dispatch(gameActions.toggleShowFocus(false));

    const props = {
      totalQuestions: gameQuestions.length,
      inCorrectAnswers,
      canClickOutside: false,
      emitOnClosePopUp: async () => {
        onClosePopUp();
        await commonUtils.sleep(400);

        goToNextQuestion(true);
      },
    } as PopUpFocusProps;

    displayPopUp(PopUpTypes.Focus, props);

    dispatch(gameActions.resetCorrectAnswers());
    dispatch(gameActions.resetIncorrectAnswers());
  };

  const showKeepGoing = () => {
    dispatch(gameActions.setKeepGoingShown());
    dispatch(gameActions.toggleShowKeepGoing(false));

    const questionsLeft = gameQuestions.slice(currentQuestionIndex + 1).length;

    const props = {
      questionsLeft,
      coins,
      canClickOutside: false,
      emitOnClosePopUp: async () => {
        onClosePopUp();
        await commonUtils.sleep(400);
        !isLevelTest && goToNextQuestion(true);
      },
    } as PopUpKeepGoingProps;

    displayPopUp(PopUpTypes.KeepGoing, props);
    if (!isLevelTest) {
      dispatch(gameActions.resetCorrectAnswers());
      dispatch(gameActions.resetIncorrectAnswers());
    }
  };

  const fetchQuizProgress = async (topicId: number, quizId: number) => {
    if (
      selectedType &&
      !isLevelTest &&
      selectedQuiz &&
      user &&
      selectedTopic &&
      !isInEndOfSkillTest &&
      !isFinalAssessment &&
      quizId
    ) {
      try {
        setLoadQuizProgress(true);

        let _questions: Question[];
        let activityHasScore: boolean | undefined;
        const cachedQuestions = questions[`${topicId}-${quizId}`];
        if (cachedQuestions) {
          _questions = cachedQuestions;
        } else {
          const result = await dispatch(questionsThunk.refreshTopicQuizQuestions()).unwrap();
          const resolved =
            result && typeof result === 'object' && 'questions' in result
              ? result
              : { questions: result };
          _questions = Array.isArray(resolved?.questions) ? resolved.questions : [];
          activityHasScore =
            result && typeof result === 'object' && 'hasScore' in result
              ? (result as { hasScore?: boolean }).hasScore
              : undefined;
        }

        if (isRead) {
          dispatch(gameActions.resetGame());

          _questions = _questions.map(question => {
            return {
              ...question,
              pickedAnswer: '',
              options: GameUtils.getPossibleAnswers(question),
            };
          });
        }

        //FIXME: Need to understand what should we use in this whitelist. Now only SkillTypes.Grammar is available, because it goes by default
        const whitelist = [
          SkillTypes.Vocabulary,
          SkillTypes.Read,
          SkillTypes.Speak,
          SkillTypes.Listen,
          SkillTypes.Grammar,
        ];

        dispatch(gameActions.resetGame());
        dispatch(gameActions.setShowPreQuiz(whitelist.includes(selectedType)));

        dispatch(
          gameActions.startGame({
            gameType: selectedQuiz.gameType ?? 1,
            questions: _questions,
          })
        );

        dispatch(gameActions.setHasScore(activityHasScore ?? selectedQuiz.hasScore));

        setLoadQuizProgress(false);
      } catch (error) {
        console.log(error);
        setLoadQuizProgress(false);
        setLoadError(true);
        commonUtils.showToast(t('anErrorOccurred'));
      }
    }
  };

  const onReset = () => {
    dispatch(gameActions.setInEndOfSkillTest(false));
    dispatch(gameActions.setSelectedQuiz(null));
    dispatch(gameActions.resetGame());
  };

  const currentQuestion = useMemo(() => {
    return isLevelTest && activity
      ? activity.questions[questionIndex]
      : gameQuestions[currentQuestionIndex];
  }, [currentQuestionIndex, gameQuestions, questionIndex]);

  const setCurrentQuestionTempData = useCallback((data: any, key?: string) => {
    if (key) {
      currentQuestionTempDataRef.current[key] = data;
    } else {
      currentQuestionTempDataRef.current = data;
    }
    setCurrentQuestionTempDataState({ ...currentQuestionTempDataRef.current });
  }, []);

  useEffect(() => {
    if (!showPreQuiz && currentQuestion) {
      if (currentQuestion?.gameType === GameTypes.ClosedSpelling) {
        let options: Block[] = [...currentQuestion.answer].map(chart => {
          return {
            id: commonUtils.generateRandomId(21),
            value: chart,
            isDisabled: false,
            isRemovable: false,
          };
        });
        const answerBlocks: Block[] = Array.from(Array(options.length)).fill(null);
        const optionBlocks: Block[] = [];
        let abcArray = [...Array(26)].map((_, i) => String.fromCharCode(i + 97));

        options.forEach((option, index) => {
          abcArray = abcArray.filter(abcChart => abcChart !== option.value);
          if (option.value === ' ') {
            answerBlocks[index] = { ...option, isDisabled: true };
          }
        });

        options = options.filter(option => option.value !== ' ');

        while (options.length < 12) {
          options.push({
            id: commonUtils.generateRandomId(21),
            value: commonUtils.getAndRemoveRandomItem(abcArray),
            isDisabled: false,
            isRemovable: true,
          });
        }

        while (options.length > 0) {
          optionBlocks.push(commonUtils.getAndRemoveRandomItem(options));
        }

        const refreshAnswerBlocks = structuredClone(answerBlocks);

        setCurrentQuestionTempData({
          refreshAnswerBlocks,
          answerBlocks,
          optionBlocks,
        });

        return;
      }

      if (currentQuestion?.gameType === NewGameTypes.Memory) {
        const gameCards = currentQuestion.newData?.data?.questionData?.pairs || [];
        const imagesAlt = currentQuestion.imagesAlt || {};

        const result: any[] = [];
        gameCards.forEach((element: any, idx: number) => {
          result.push({
            data: element.text,
            index: idx,
            isMatched: false,
            isSelected: false,
          });
          result.push({
            data: element.image,
            imageText: element.imageSource || element.imageCredit,
            imageAlt: imagesAlt[element.image] || '',
            index: idx,
            isMatched: false,
            isSelected: false,
          });
        });

        const quizData: any[] = commonUtils.shuffleArray(result);

        setCurrentQuestionTempData({ quizData });

        return;
      }

      if (currentQuestion?.gameType === NewGameTypes.Read) {
        return;
      }
    }

    setCurrentQuestionTempData({});
  }, [currentQuestion, showPreQuiz]);

  const checkAnswer = async () => {
    const currentAnswer = isLevelTest
      ? currentQuestion.data.questionData.answer.text
      : currentQuestion.answer;
    let isAnswerCorrect = pickedAnswer == currentAnswer;
    const isListen = selectedType == SkillTypes.Listen;

    if (
      pickedAnswer &&
      (gameTypeReal === GameTypes.Pronunciation ||
        gameTypeReal === NewGameTypes.Speak ||
        (gameTypeReal === NewGameTypes.NaturalConversation &&
          gameQuestions[currentQuestionIndex]?.newData?.data?.metadata?.skillId ===
            GameTypes.Pronunciation))
    ) {
      const lowerCaseQuestion = currentQuestion.question?.toLowerCase();
      const lowerCasePickedAnswer = pickedAnswer.toLocaleLowerCase();

      const isSimilar = commonUtils.isSimilar(lowerCaseQuestion, lowerCasePickedAnswer);

      isAnswerCorrect = isSimilar;
    }

    if (
      (gameType !== GameTypes.ClosedSpelling &&
        gameType !== GameTypes.Pronunciation &&
        (!isListen || isAnswerCorrect || !allowRetry)) ||
      (isListen && (isLevelTest || isFinalAssessment))
    ) {
      await commonUtils.sleep(1500); // Wait before checking
    }

    if (!isMountedRef.current || gameStatusRef.current !== GameStatus.CHECK_ANSWER) {
      return;
    }

    if (!selectedQuiz && !isInEndOfSkillTest && !isLevelTest) {
      return;
    }

    let visitFocus = false;
    let visitKeepGoing = false;

    const displayFocusAfterX = GameUtils.displayFocusAfterX(selectedQuiz as Quiz);
    const displayKeepGoingAfterX = GameUtils.displayKeepGoingAfterX(selectedQuiz as Quiz);

    // fix show FocusModal and KeepGoingModal regarding skipped questions
    let inCorrectQuestionAnswers = inCorrectAnswers;
    let correctQuestionAnswers = correctAnswers;
    let total_inCorrectQuestionAnswers = total_inCorrectAnswers;
    if (
      gameTypeReal === GameTypes.Pronunciation ||
      gameTypeReal === NewGameTypes.Speak ||
      gameTypeReal === NewGameTypes.NaturalConversation
    ) {
      const correctAnswersQuestions = (quizQuestionResultData || []).filter(q => q.isCorrect);
      const inCorrectAnswersQuestions = (quizQuestionResultData || []).filter(
        q => !q.isCorrect && q.questionInputStatus !== 'NoInput'
      );
      inCorrectQuestionAnswers = inCorrectAnswersQuestions.length;
      total_inCorrectQuestionAnswers = inCorrectAnswersQuestions.length;
      correctQuestionAnswers = correctAnswersQuestions.length;
    }

    const displayActionModals = GameUtils.shouldDisplayActionModals(gameQuestions ?? []);

    if (
      displayActionModals &&
      displayFocusAfterX !== null &&
      displayFocusAfterX > 0 &&
      inCorrectQuestionAnswers >= displayFocusAfterX
    ) {
      visitFocus = true;
    }

    if (
      displayActionModals &&
      displayKeepGoingAfterX != null &&
      displayKeepGoingAfterX > 0 &&
      correctQuestionAnswers >= displayKeepGoingAfterX
    ) {
      visitKeepGoing = true;
    }

    if (visitKeepGoing && visitFocus) {
      visitKeepGoing = false;
    }

    if (
      visitFocus &&
      !focusShown &&
      !GameUtils.isOneQuestionLeft(currentQuestionIndex, gameQuestions)
    ) {
      if (
        ignoreFocusTypes.includes(gameType) ||
        (isListen && allowRetry) ||
        toSkipThisSpeechRecordingQuestion()
      ) {
        dispatch(gameActions.toggleShowFocus(true));
      } else {
        showFocusModal();
        return;
      }
    } else if (focusShown && total_inCorrectQuestionAnswers > (displayFocusAfterX ?? 0)) {
      dispatch(gameActions.endGame());
      return;
    }

    if (
      visitKeepGoing &&
      !keepGoingShown &&
      !GameUtils.isOneQuestionLeft(currentQuestionIndex, gameQuestions)
    ) {
      if (
        ignoreFocusTypes.includes(gameType) ||
        (isListen && allowRetry) ||
        toSkipThisSpeechRecordingQuestion()
      ) {
        dispatch(gameActions.toggleShowKeepGoing(true));
      } else {
        showKeepGoing();
        return;
      }
    }

    if (isListen && !isAnswerCorrect && (isInEndOfSkillTest || isFinalAssessment)) {
      await commonUtils.sleep(1500);
    }

    if (!isMountedRef.current || gameStatusRef.current !== GameStatus.CHECK_ANSWER) {
      return;
    }

    if (
      isListen &&
      !isAnswerCorrect &&
      !isLevelTest &&
      !isInEndOfSkillTest &&
      !isFinalAssessment &&
      allowRetry
    ) {
      dispatch(gameActions.setPictureOverlay(true));
      return;
    }

    if (isLevelTest) {
      const answer = {
        questionId: currentQuestion.questionId,
        isCorrect: isAnswerCorrect,
        wrongAnswer: isAnswerCorrect ? null : pickedAnswer,
        isTimesUp: false,
        responseTime: 0,
        gameTypeId: currentQuestion.gameTypeId,
      };
      dispatch(levelTestActions.setAnswer(answer));
    }

    dispatch(gameActions.setCheckAnswerCompleted(true));

    //FIXME: Need to remove, because we don't use SkillTypes
    // if (selectedType == SkillTypes.Spelling || selectedType == SkillTypes.Speak) {
    //   return;
    // }
    if (
      gameType == GameTypes.ClosedSpelling ||
      gameType == GameTypes.Pronunciation ||
      gameTypeReal === NewGameTypes.Speak ||
      (gameTypeReal === NewGameTypes.NaturalConversation &&
        gameQuestions[currentQuestionIndex]?.newData?.data?.metadata?.skillId ===
          GameTypes.Pronunciation)
    ) {
      return;
    }

    const isLast = currentQuestionIndex === gameQuestions.length - 1;

    if (isLevelTest && isRead) {
      return;
    } else if (isRead && isLast) {
      return;
    }
    if (isLevelTest) {
      startInitialLevelTestGame(questionIndex + 1);
    }

    goToNextQuestion();
  };

  const displayPopUp = (
    type: PopUpTypes,
    props: PopUpKeepGoingProps | PopUpFocusProps | PopUpTimeIsUpProps | PopUpSummaryProps
  ) => {
    setPopUpType(type);
    setPopUpProps(props);
    setShouldDisplayPopUp(true);
  };

  const onMute = () => {
    dispatch(gameActions.toggleGameMute());
  };

  const onPause = async () => {
    if (
      gameStatus === GameStatus.ENDED ||
      gameStatus === GameStatus.TIME_ENDED ||
      gameStatus === GameStatus.SHOW_HINTS ||
      gameStatus === GameStatus.SHOW_DICTIOANRY ||
      gameStatus === GameStatus.PREPARING ||
      isLevelTest
    ) {
      return;
    }

    if (
      gameStatus === GameStatus.PLAYING ||
      gameStatus === GameStatus.QUESTION_ANSWERED ||
      gameStatus === GameStatus.CHECK_ANSWER
    ) {
      dispatch(
        gameActions.onPause({
          simple:
            gameStatus === GameStatus.QUESTION_ANSWERED || gameStatus === GameStatus.CHECK_ANSWER,
        })
      );
    } else {
      dispatch(gameActions.onPlay());
    }
  };

  useEffect(() => {
    if (!isPageVisible) {
      if (
        (gameStatus === GameStatus.PLAYING ||
          gameStatus === GameStatus.QUESTION_ANSWERED ||
          gameStatus === GameStatus.CHECK_ANSWER) &&
        !showPreQuiz &&
        !isLevelTest
      ) {
        pausedByTabSwitchRef.current = true;
        if (!(pauseTimer && document.querySelector('[data-hide_quiz_pause_modal="true"]'))) {
          dispatch(
            gameActions.onPause({
              simple:
                gameStatus === GameStatus.QUESTION_ANSWERED ||
                gameStatus === GameStatus.CHECK_ANSWER,
            })
          );
        }
      }
    } else {
      pausedByTabSwitchRef.current = false;
    }
  }, [isPageVisible, gameStatus, dispatch, pauseTimer, showPreQuiz, isLevelTest]);

  const shouldBlockNavigation = useCallback(
    ({ historyAction }: { historyAction: string }) =>
      historyAction === 'POP' &&
      (gameStatus === GameStatus.PLAYING ||
        gameStatus === GameStatus.QUESTION_ANSWERED ||
        gameStatus === GameStatus.CHECK_ANSWER) &&
      !showPreQuiz &&
      !isLevelTest,
    [gameStatus, showPreQuiz, isLevelTest]
  );

  const navigationBlocker = useBlocker(shouldBlockNavigation);

  useEffect(() => {
    if (navigationBlocker.state === 'blocked') {
      dispatch(
        gameActions.onPause({
          simple:
            gameStatus === GameStatus.QUESTION_ANSWERED || gameStatus === GameStatus.CHECK_ANSWER,
        })
      );
      navigationBlocker.reset?.();
    }
  }, [navigationBlocker, dispatch, gameStatus]);

  const onClosePopUp = async () => {
    setShouldDisplayPopUp(false);
    setPopUpType(null);
    setPopUpProps(null);

    if (
      popUpType === PopUpTypes.Pause ||
      popUpType === PopUpTypes.Hints ||
      popUpType === PopUpTypes.WordsList
    ) {
      await commonUtils.sleep(400);
      dispatch(gameActions.onPlay());
    }
  };

  const closeHintPopup = async () => {
    onClosePopUp();
    dispatch(gameActions.setSelectedHint({ hint: null, soundPath: '' }));
    dispatch(gameActions.onPlay());
  };

  const addCoins = (coins: number) => {
    const currentCoins = user?.progress?.coins ?? 0;

    dispatch(authActions.setUserCoins(currentCoins + coins));
  };

  const onHintClick = async (hint: HintsType) => {
    const soundPath = gameQuestions[currentQuestionIndex].dictionaryDetails?.audio || '';

    addCoins(user?.metadata.role === 'teacher' ? -0 : -5);

    dispatch(gameActions.setSelectedHint({ hint, soundPath }));

    closeHintPopup();
  };

  const goToNextQuestion = (force = false) => {
    if (
      !force &&
      gameType !== NewGameTypes.NaturalConversation &&
      !toSkipThisSpeechRecordingQuestion()
    ) {
      if (visitFocus) {
        showFocusModal();
        return;
      }
      if (visitKeepGoing && !visitFocus) {
        showKeepGoing();
        return;
      }
    }

    dispatch(
      gameActions.goToNextQuestion({
        quizzesLength: levelTestQuizzes?.length ?? 0,
        levelTestQuizIndex: levelTestQuizIndex,
        shouldDisplayPopUp: shouldDisplayPopUp,
        popUpType: popUpType,
      })
    );
  };

  const toggleCheckAnswer = async (answer: string) => {
    dispatch(
      gameActions.handlePickedAnswer({
        answer,
      })
    );

    let showAnswer = true;

    if (
      selectedType === SkillTypes.Listen &&
      allowRetry &&
      !isLevelTest &&
      !isInEndOfSkillTest &&
      !isFinalAssessment
    ) {
      showAnswer = false;
    }

    if (showAnswer && isLevelTest) {
      await commonUtils.sleep(500);
      dispatch(gameActions.setShowAnswerInOptions());
    }
    if (
      isLevelTest &&
      questionIndex === 9 &&
      activity.title === 'INITIAL_LEVEL_TEST_LEVEL_4' &&
      levelTestCorrectAnswers >= 6
    ) {
      const modalProps = {
        emitOnClosePopUp: async () => {
          setShouldDisplayPopUp(false);
          setPopUpType(null);
          setPopUpProps(null);
        },
      } as PopUpSummaryProps;
      displayPopUp(PopUpTypes.KeepGoing, modalProps);
    }
  };

  //FIXME: NEED TO SPLIT THIS FUNCTION TO SMALL ONE. IT'S TOO COMPLICATED
  const reactToGameStatus = async () => {
    if (gameStatus === GameStatus.CHECK_ANSWER) {
      await checkAnswer();
    } else if (gameStatus === GameStatus.ENDED) {
      const _selectedTopic = selectedTopic;
      const _selectedQuiz = selectedQuiz;

      dispatch(
        appSettingsActions.clearTopicQuizzes({
          topicId: Number(selectedTopic?.id),
        })
      );

      dispatch(
        appSettingsActions.clearQuestionsProgress({
          topicId: Number(selectedTopic?.id),
          quizId: Number(selectedQuiz?.id),
        })
      );

      let progressSaveError = false;
      let isLastActivityForCanDo = false;
      let isLastQuizInTopic = false;
      if (!isLevelTest && !isTeacher) {
        setSavingQuizProgressData(true);
        try {
          const { payload: quizData, isLastActivityForCanDo: _isLastActivityForCanDo } =
            buildEndGameBody();
          isLastActivityForCanDo = _isLastActivityForCanDo;

          await progressService.sendQuizProgressData(user, quizData);
          dispatch(authActions.refreshUserProgress());

          await dispatch(quizzesThunks.refreshTopicQuizzes({ updateUserData: true })).unwrap();

          isLastQuizInTopic = Boolean(quizData.isLastActivityInTopic);
        } catch (error) {
          console.log(error);
          progressSaveError = true;
          commonUtils.showGlobalError({
            i18nKey: 'error.somethingWentWrong',
            navigateOnClose: '/dashboard/games',
          });
        }
        setSavingQuizProgressData(false);
      } else {
        dispatch(authActions.refreshUserProgress());
      }

      // Only show quizSummary modal if there was no error saving progress
      if (progressSaveError) {
        return;
      }

      const nextTopicQuiz = await getNextTopicQuiz();

      const isLastActivity = GameUtils.isLastActivity(
        selectedQuiz?.order,
        selectedTopic?.activitiesInTopic
      );

      const props = {
        selectedSkill,
        selectedTopic,
        selectedQuiz,
        correctAnswers: total_correctAnswers,
        totalQuestions: gameQuestions.length,
        coins,
        hasNextQuiz: !isLastActivity,
        userData: _selectedQuiz?.userData,
        isLastActivityForCanDo,
        emitOnClosePopUp: async (hasFailed: boolean) => {
          onClosePopUp();
          if (hasFailed && _selectedQuiz && _selectedTopic) {
            dispatch(gameActions.resetGame());
            fetchQuizProgress(_selectedTopic.id, _selectedQuiz.id);
          } else if (nextTopicQuiz) {
            onReset();
            dispatch(gameActions.setSelectedQuiz(nextTopicQuiz.quiz));
            dispatch(gameActions.setSelectedTopic(nextTopicQuiz.topic as any));
            if (nextTopicQuiz.quiz && selectedTopicNew && selectedUnit) {
              dispatch(
                activityLocationsActions.setActivityLocation({
                  activityId: nextTopicQuiz.quiz.id,
                  topic: selectedTopicNew,
                  activitiesInTopic: nextTopicQuiz.topic?.activitiesInTopic,
                  unit: selectedUnit,
                  level: selectedLevel,
                })
              );
            }
            dispatch(
              unitActions.setSelectedTopic({
                id: nextTopicQuiz.newTopic?.id || 0,
                name: nextTopicQuiz.newTopic?.name,
                cEFRDescriptor: nextTopicQuiz.newTopic?.cEFRDescriptor,
                canDoStatement: nextTopicQuiz.newTopic?.canDoStatement,
              })
            );

            fetchQuizProgress(nextTopicQuiz?.topic?.id ?? 0, nextTopicQuiz?.quiz?.id ?? 0);
            await commonUtils.sleep(1000);
          } else {
            await commonUtils.sleep(400);
            navigate('/dashboard/games');
          }
        },
        emitOnExit: onExit,
        emitOnExitWithCanDo: () => {
          const props = {
            emitOnExit: onExit,
            selectedTopic,
            selectedQuiz,
            quizzes,
            isLastQuizInTopic,
          };
          displayPopUp(PopUpTypes.CanDoStatement, props);
        },
        emitOnQuit: onQuit,
        emitOnAknowledge: onAknowledge,
        emitOnEndOfSkillTest: async () => {
          const nextTopic = getNextTopic();

          if (nextTopic) {
            onClosePopUp();
          }
        },
        emitStartQuizFromPractice: () => {
          onClosePopUp();
          dispatch(gameActions.resetGame());
          if (_selectedQuiz) {
            dispatch(
              gameActions.startGame({
                gameType: _selectedQuiz.gameType,
                questions: gameQuestions,
              })
            );
            dispatch(gameActions.setHasScore(_selectedQuiz.hasScore));
          }
        },
        emitPracticeAgain: () => {
          onClosePopUp();
          dispatch(gameActions.resetGame());

          if (_selectedQuiz) {
            dispatch(
              gameActions.startGame({
                gameType: +selectedQuiz.gameType,
                questions: gameQuestions,
              })
            );
            dispatch(gameActions.setHasScore(_selectedQuiz.hasScore));
          }
          dispatch(gameActions.setPracticeGame());
        },
      } as PopUpSummaryProps;

      let totalNumberOfQuestions = 0;
      if (isRead) {
        totalNumberOfQuestions = GameUtils.getTotalNumberOfQuestionsWithScore(gameQuestions);
      }

      const averageScore = GameUtils.calculateScore(quizQuestionResultData, totalNumberOfQuestions);
      const isFailed = GameUtils.isFailed(averageScore);

      if (!isFailed) {
        addCoins(coins);
      }

      if (!isLevelTest) {
        displayPopUp(PopUpTypes.Summary, props);
      }
    } else if (gameStatus === GameStatus.TIME_ENDED) {
      dispatch(gameActions.setRevealTranslation(false));

      if (!timesUpShown) {
        dispatch(gameActions.setTimesUpShown());
      }

      // Show times up modal
      const props = {
        emitOnClosePopUp: async () => {
          onClosePopUp();
          onDisableRetry();
        },
        isLastQuestion: GameUtils.isLastQuestion(gameQuestions, currentQuestionIndex),
        allowRetry: allowRetry,
        emitOnNextQuestion: async () => {
          onClosePopUp();
          await commonUtils.sleep(200);
          dispatch(
            gameActions.handlePickedAnswer({
              answer: '',
            })
          );
          goToNextQuestion(true);
        },
      } as PopUpTimeIsUpProps;

      displayPopUp(PopUpTypes.TimeIsUp, props);
    } else if (
      (gameStatus === GameStatus.PAUSED || gameStatus === GameStatus.PAUSED_SIMPLE) &&
      !isLevelTest
    ) {
      const props = {
        currentQuestion: currentQuestionIndex,
        totalQuestions: gameQuestions.length,
        coins,
        emitOnClosePopUp: async () => {
          onClosePopUp();
          await commonUtils.sleep(400);

          dispatch(gameActions.onPlay());
        },
        emitOnQuit: onExit,
      } as PopUpPauseProps;
      displayPopUp(PopUpTypes.Pause, props);
    } else if (gameStatus === GameStatus.SHOW_HINTS) {
      const props = {
        coins: currentCoins,
        emitOnSelectHint: onHintClick,
        emitOnClosePopUp: async () => {
          onClosePopUp();
          await commonUtils.sleep(400);
          dispatch(gameActions.onPlay());
        },
      } as PopUpHintsProps;

      displayPopUp(PopUpTypes.Hints, props);
    } else if (gameStatus === GameStatus.SHOW_WORDS_LIST) {
      const props = {
        emitOnClosePopUp: async () => {
          onClosePopUp();
          await commonUtils.sleep(400);
          dispatch(gameActions.onPlay());
        },
        emitOnAknowledge: onAknowledge,
      } as PopUpWordsListProps;

      displayPopUp(PopUpTypes.WordsList, props);
    } else if (gameStatus === GameStatus.OPEN_WRITING_AI_REVIEW) {
      const props = {
        selectedTopic,
        emitOnNextQuestion: async () => {
          onClosePopUp();
          await commonUtils.sleep(400);
          // dispatch(gameActions.onPlay());
          goToNextQuestion(true);
        },
        emitOnRetry: async () => {
          onClosePopUp();
          await commonUtils.sleep(400);
          dispatch(gameActions.onPlay());
        },
        emitOnQuit: onExit,
        //FIXME: Need to change any for needed type
      } as any;

      displayPopUp(PopUpTypes.OpenWrittingAIReview, props);
    } else if (gameStatus === GameStatus.FINISH_UNIT) {
      const props = {
        emitOnQuit: onExit,
      } as any;

      displayPopUp(PopUpTypes.FinishUnit, props);
    }
  };

  type BuildEndGameBodyResult = {
    payload: any;
    isLastActivityForCanDo: boolean;
  };

  // Falls back to the (possibly stale) selectedTopic/selectedUnit on a miss so a
  // submission is never blocked; a miss should only happen if a future entry
  // point sets selectedQuiz without also writing to the map.
  const resolveActivityTopicAndUnit = (activityId: number | undefined) => {
    const activityLocation = activityId != null ? activityLocationsById[activityId] : undefined;

    if (!activityLocation) {
      console.warn('ActivityLocationMapMiss: no known location for activityId', activityId);
    }

    return {
      topicId: activityLocation?.topic?.id ?? selectedTopic?.id,
      unitId: activityLocation?.unit?.id ?? selectedUnit?.id,
    };
  };

  const buildEndGameBody = (): BuildEndGameBodyResult => {
    const finishTime = new Date();
    if (gameTypeReal === GameTypes.Practice) {
      return {
        payload: buildPracticeEndGameBody(finishTime),
        isLastActivityForCanDo: false,
      };
    }
    const progressForCalc = user?.progress;
    const workingQuizData: QuizQuestionResult[] = quizQuestionResultData;
    let scoreEarned = 0;
    let noGrading = false;
    let isSkipped = false;
    let achievedCoins = coins;
    let hasIssues = false;
    const isCompletedBefore = selectedQuiz?.userData?.isCompleted ?? false;
    const isSpeak = gameTypeReal === NewGameTypes.Speak;
    if (gameTypeReal === NewGameTypes.OpenWriting) {
      const gradedResults = quizQuestionResultData.filter(
        result => result.openWritingResult?.scoreResponse?.noGrading !== true
      );
      const scores: number[] = gradedResults
        .map(result => result.openWritingResult?.scoreResponse?.overallScore)
        .filter((score): score is number => typeof score === 'number');
      const totalScores = scores.reduce((sum, score) => sum + score, 0);
      scoreEarned = scores.length > 0 ? commonUtils.roundNumber(totalScores / scores.length, 0) : 0;

      // Check if there are any issues (vulgar language, gibberish, non-English) in any open writing responses
      hasIssues = quizQuestionResultData.some(result => {
        const openWritingResult = result.openWritingResult;
        if (!openWritingResult) return false;

        const scoreResponse = openWritingResult.scoreResponse;
        const question = gameQuestions.find(q => q.questionId === result.questionId);
        const isResponseInEnglish = question?.newData?.data?.metadata?.isResponseInEnglish;

        const hasVulgar = scoreResponse?.containsVulgarLanguage;
        const hasGibberish = scoreResponse?.containsGibberish;
        const hasNonEnglish = isResponseInEnglish && !isOpenWritingAnswerInEnglish;

        return hasVulgar || hasGibberish || hasNonEnglish;
      });

      if (gradedResults === undefined || gradedResults.length == 0) {
        noGrading = !hasIssues;
      }

      if (hasIssues) {
        achievedCoins = 0;
      } else {
        achievedCoins = isCompletedBefore ? 0 : quizQuestionResultData.length * 3;
      }
    } else {
      if (isSpeak) {
        const isActivitySkipped = GameUtils.isSkipped(quizQuestionResultData);
        if (isActivitySkipped) {
          isSkipped = true;
        }
      }

      let totalNumberOfQuestions = 0;
      if (isRead) {
        totalNumberOfQuestions = GameUtils.getTotalNumberOfQuestionsWithScore(gameQuestions);
      }

      scoreEarned = GameUtils.calculateScore(workingQuizData, totalNumberOfQuestions);
      const isFailed = GameUtils.isFailed(scoreEarned);
      if (isFailed) {
        achievedCoins = 0;
      } else if (isSpeak && !isSkipped) {
        achievedCoins = isCompletedBefore ? 0 : total_correctAnswers * 3;
      }
    }
    const stars = noGrading ? 3 : GameUtils.getStarsAmount(scoreEarned);
    const isCompletedNow = noGrading || isSkipped ? true : scoreEarned >= 60;
    const isCompleted = isCompletedBefore || isCompletedNow;
    const isFailed = !isSkipped && stars === 0;

    const quizQuestionResultDataIds = workingQuizData.map(
      (question: QuizQuestionResult) => question.questionId
    );
    const remainingQuestionBuild: QuizQuestionResult[] = [];
    const topicId = selectedTopic?.id;
    const unitId = resolveProgressUnitId({
      isTeacher,
      topics,
      topicId,
      selectedUnitId: selectedUnit?.id,
    });
    const unitTopics = unitId != null ? topics[unitId] : undefined;

    const {
      isLastActivityInTopic: isLastQuizInTopic,
      isLastActivityInUnit,
      isLastActivityInLevel,
    } = GameUtils.getLastActivityFlags({
      unitId,
      topicId,
      activitiesInTopic: selectedTopic?.activitiesInTopic,
      progress: progressForCalc,
      unitTopics,
      units,
      selectedUnitOrder: units.find(u => u.id === unitId)?.order ?? selectedUnit?.order,
      isCompletedNow,
      isCompletedBefore,
      isTeacher,
    });

    const completedCount = GameUtils.getTopicCompletedCount(progressForCalc, unitId, topicId);
    const activitiesInTopic = selectedTopic?.activitiesInTopic ?? 0;
    const completedCountWithCurrentActivity =
      isCompletedNow && !isCompletedBefore ? completedCount + 1 : completedCount;
    const allActivitiesCompleted =
      activitiesInTopic > 0 && completedCountWithCurrentActivity >= activitiesInTopic;
    const previousScore = selectedQuiz?.userData?.cumulativeScore ?? 0;
    const scoreImproved = scoreEarned > previousScore;
    const isLastActivityForCanDo =
      !isTeacher &&
      ((isLastQuizInTopic && !allActivitiesCompleted) ||
        (allActivitiesCompleted && scoreImproved && !isFailed));

    const remainingQuestion: Question[] = gameQuestions.filter(
      question => !quizQuestionResultDataIds.includes(question.questionId)
    );
    if (remainingQuestion.length > 0) {
      remainingQuestion.forEach((question, index) => {
        remainingQuestionBuild.push({
          questionId: question.questionId,
          isCorrect: false,
          isTimesUp: true,
          wrongAnswer: null,
          responseTime: 0,
          achievedCoins: 0,
          coinsUsed: hintsData[index].coinsUsed,
          hintUsageCount: hintsData[index].hintUsageCount,
          gameType: question.gameType,
          isQuestionPresented: false,
          hasScore:
            gameTypeReal === NewGameTypes.OpenWriting
              ? question.newData?.data?.metadata?.hasScore ?? selectedQuiz?.hasScore ?? gameHasScore
              : question.newData.data.metadata.hasScore,
        });
      });
    }
    const quizQuestionResults = workingQuizData.map((question: QuizQuestionResult) => {
      return {
        ...question,
        isQuestionPresented: true,
      };
    });

    const questionsForPayload = [...quizQuestionResults, ...remainingQuestionBuild];
    const openWritingHasScoreFromQuestions =
      gameTypeReal === NewGameTypes.OpenWriting &&
      questionsForPayload.some((q: QuizQuestionResult) => q.hasScore);
    const payloadHasScore =
      gameTypeReal === NewGameTypes.OpenWriting
        ? openWritingHasScoreFromQuestions || (selectedQuiz?.hasScore ?? gameHasScore)
        : selectedQuiz?.hasScore;

    const content = {
      questions: questionsForPayload,
      activityId: selectedQuiz?.id,
      topicId,
      unitId,
      correctAnswers: total_correctAnswers,
      wrongAnswers: total_inCorrectAnswers,
      isCompleted: isCompleted,
      achievedStars: stars,
      achievedCoins: achievedCoins,
      coinsUsed: hintsData
        .map(data => data.coinsUsed)
        .reduce((accumulator, currentValue) => accumulator + currentValue, 0),
      hintUsageCount: hintsData
        .map(data => data.hintUsageCount)
        .reduce((accumulator, currentValue) => accumulator + currentValue, 0),
      questionsAnswered: total_correctAnswers + total_inCorrectAnswers,
      hasScore: payloadHasScore,
      score: scoreEarned,
      startTime: totalQuizTime,
      finishTime: finishTime.toISOString(),
      isLastActivityInTopic: isLastQuizInTopic,
      isLastActivityInUnit: isLastActivityInUnit,
      IsLastActivityInLevel: isLastActivityInLevel,
      //FIXME: Need to fix any type
      isPracticeMode: gameTypeReal === (GameTypes.Practice as any),
      user: {
        groupId: user?.metadata?.organizationDetails?.groupId,
        organizationId: user?.metadata?.organizationDetails?.organizationId,
        userLevel: user?.progress?.userLevel,
        userNativeLanguage: user?.metadata?.languagePreferences?.nativeLanguage,
        userInterfaceLanguage: user?.metadata?.languagePreferences?.interfaceLanguage,
      },
      ...(isSpeak && { isSkipped }),
      clientType: isMobile ? 'WEB_MOBILE' : 'WEB_DESKTOP',
      timezoneOffsetMinutes: new Date().getTimezoneOffset(),
    };
    if (isSkipped && isSpeak) {
      content.isSkipped = true;
      content.score = 0;
      content.achievedCoins = 0;
      content.achievedStars = 0;
    }
    return { payload: content, isLastActivityForCanDo };
  };

  const buildPracticeEndGameBody = (finishTime: Date) => {
    const practiceQuestions: any = [];
    gameQuestions.map(question => {
      practiceQuestions.push({
        questionId: question.questionId,
        achievedCoins: 0,
        isCorrect: true,
        wrongAnswer: null,
        isTimesUp: true,
        hintUsageCount: 0,
        coinsUsed: 0,
        responseTime: 0,
        gameType: question.gameType,
        isQuestionPresented: true,
        practice: question.userData?.practice?.isKnown
          ? {
              isKnown: question.userData?.practice?.isKnown,
            }
          : null,
      });
    });
    const practiceContent = {
      questions: practiceQuestions,
      activityId: selectedQuiz?.id,
      topicId: selectedTopic?.id,
      unitId: selectedUnit?.id,
      correctAnswers: 0,
      wrongAnswers: 0,
      isCompleted: false,
      achievedStars: 0,
      achievedCoins: 0,
      coinsUsed: 0,
      hintUsageCount: 0,
      questionsAnswered: 0,
      score: 0,
      startTime: totalQuizTime,
      finishTime: finishTime.toISOString(),
      isLastActivityInTopic: false,
      isLastActivityInUnit: false,
      IsLastActivityInLevel: false,
      //FIXME: Need to fix any type
      isPracticeMode: gameTypeReal === (GameTypes.Practice as any),
      user: {
        groupId: user?.metadata?.organizationDetails?.groupId,
        organizationId: user?.metadata?.organizationDetails?.organizationId,
        userLevel: user?.progress?.userLevel,
        userNativeLanguage: user?.metadata?.languagePreferences?.nativeLanguage,
        userInterfaceLanguage: user?.metadata?.languagePreferences?.interfaceLanguage,
      },
    };
    return practiceContent;
  };

  const onDisableRetry = async () => {
    dispatch(gameActions.disableRetry());
    await commonUtils.sleep(400);
    dispatch(
      //FIXME: NEED TO REPLACE when newData will be changed
      gameActions.resetTimer(+gameQuestions[currentQuestionIndex].newData.data.metadata.timer)
    );

    // Deduct total incorrect answers and incorrect answers
    // Remove question result data from array
    // Because they are retrying.
    if (
      //FIXME: Need to remove SkillTypes, because we don't use it
      // selectedType === SkillTypes.Spelling ||
      // selectedType === SkillTypes.Listen ||
      // selectedType === SkillTypes.Speak
      selectedType === SkillTypes.Listen ||
      gameTypeReal === GameTypes.ClosedSpelling ||
      gameTypeReal === GameTypes.Pronunciation ||
      gameTypeReal === NewGameTypes.Speak ||
      (gameTypeReal === NewGameTypes.NaturalConversation &&
        gameQuestions[currentQuestionIndex]?.newData?.data?.metadata?.skillId ===
          GameTypes.Pronunciation)
    ) {
      dispatch(gameActions.resetCurrentQuestionState());
    }
  };

  const onQuit = async () => {
    onClosePopUp();
    dispatch(gameActions.resetAllSelected());
    onReset();
    navigate('/dashboard/games');
  };

  const onExit = async () => {
    onClosePopUp();
    onReset();
    navigate('/dashboard/games');
  };

  const onStartQuiz = () => {
    dispatch(gameActions.setShowPreQuiz(false));
  };

  const onPractice = () => {
    onStartQuiz();

    if (user?.metadata.languagePreferences.fromToLanguageParams) {
      dispatch(
        gameActions.setFromToLang(user?.metadata.languagePreferences.fromToLanguageParams[0])
      );
    }

    if (!loadQuizProgress) {
      dispatch(gameActions.setPracticeGame());
    }
  };

  const onAknowledge = (id: number, isKnown: boolean) => {
    dispatch(gameActions.setWordKnowledge({ id, isKnown }));
  };

  const onSwipe = (questionId: number) => {
    dispatch(gameActions.onSwipe(questionId));
  };
  //TODO: Need to understand if it's needed
  // const onLoadLevelTestQuiz = (quiz: Quiz) => {
  //   if (quiz.questions) {
  //     dispatch(gameActions.resetGame());
  //     dispatch(gameActions.setSelectedQuiz(quiz));
  //     dispatch(
  //       gameActions.startGame({
  //         gameType: quiz.gameType,
  //         questions: quiz.questions,
  //       })
  //     );
  //   }
  // };

  useEffect(() => {
    reactToGameStatus();
  }, [gameStatus]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;

      if (!isLevelTest) {
        dispatch(gameActions.resetGame());
      }
    };
  }, []);

  useEffect(() => {
    if (!selectedQuiz) {
      if (isLevelTest) {
        //TODO: Need to understand if it's needed
        // const firstUnfinished = levelTestQuizzes.find(
        //   (quiz) => !quiz.isLocked && !quiz.isFinished
        // );

        // if (firstUnfinished) {
        // onLoadLevelTestQuiz(firstUnfinished);
        // }

        return;
      }

      return navigate('/dashboard/games');
    }

    const { topicId } = resolveActivityTopicAndUnit(selectedQuiz.id);
    fetchQuizProgress(topicId ?? 0, selectedQuiz.id);
  }, []);

  // selectedTopic/selectedUnit can be left over from browsing a different topic
  // before this quiz was opened (see ELL-7909). Correct them against the
  // activityLocations map as soon as this activity opens, so every other
  // place in the app that reads selectedTopic/selectedUnit directly (topic
  // name shown in the UI, "last activity in topic/unit" logic, etc.) sees the
  // right value too, not just the two places already patched to consult the
  // map directly. The same browser-back navigation can also leave the topics
  // drawer open behind this page (it's only ever closed via goToQuizPage,
  // which a back-navigation bypasses), so close it here too whenever a quiz
  // is actually showing.
  useEffect(() => {
    if (!selectedQuiz) {
      return;
    }

    drawer.closeTopicsDrawer();

    const activityLocation = activityLocationsById[selectedQuiz.id];
    if (!activityLocation) {
      return;
    }

    const topicMismatch = selectedTopic?.id !== activityLocation.topic.id;
    const unitMismatch = selectedUnit?.id !== activityLocation.unit.id;

    if (topicMismatch) {
      // Mirrors the existing (FIXME-tagged) shape used in UnitsRoadmapCarousel
      // when converting a topic into the game slice's selectedTopic.
      const mock = {
        id: activityLocation.topic.id,
        nameLocal: null,
        name: activityLocation.topic.name,
        order: 1,
        quizzesCount: 1,
        topicType: 1,
        activitiesInTopic: activityLocation.activitiesInTopic,
        userData: {
          receivedStars: 8,
          quizzesCompleted: 3,
        },
      };
      dispatch(gameActions.setSelectedTopic(mock));
      dispatch(
        unitActions.setSelectedTopic({
          id: activityLocation.topic.id,
          name: activityLocation.topic.name,
          cEFRDescriptor: activityLocation.topic.cEFRDescriptor,
          canDoStatement: activityLocation.topic.canDoStatement,
        })
      );
    }

    if (unitMismatch) {
      dispatch(
        unitActions.setSelectedUnit({
          id: activityLocation.unit.id,
          name: activityLocation.unit.name,
          order: activityLocation.unit.order,
        })
      );
    }
  }, [selectedQuiz?.id, activityLocationsById, selectedTopic?.id, selectedUnit?.id]);

  useEffect(() => {
    if (
      gameStatus === GameStatus.PLAYING &&
      !currentTime &&
      !isLevelTest &&
      !isInEndOfSkillTest &&
      !isFinalAssessment
    ) {
      if (isRead || isMemory) {
        dispatch(gameActions.endGame());
        return;
      }

      dispatch(gameActions.timeOutGame());
    }
  }, [gameStatus, currentTime]);

  // manage question timer until Read game
  useEffect(() => {
    if (
      !isRead &&
      gameQuestions?.length &&
      gameQuestions[currentQuestionIndex]?.newData?.data?.metadata?.timer
    ) {
      dispatch(
        gameActions.setCurrentTimer(
          //FIXME: NEED TO REPLACE when newData will be changed
          +gameQuestions[currentQuestionIndex]?.newData?.data?.metadata?.timer
        )
      );
    }
  }, [currentQuestionIndex, gameQuestions, isRead]);

  // manage Read game timer
  const lastGameStatus = useRef<GameStatus | null>(null);
  useEffect(() => {
    if (isRead && gameQuestions?.length && gameQuestions[0]?.newData?.data?.metadata?.timer) {
      if (
        // start new game
        gameStatus === GameStatus.PREPARING ||
        (gameStatus === GameStatus.PLAYING && lastGameStatus.current === GameStatus.PREPARING) ||
        // retry game
        (gameStatus === GameStatus.PLAYING && lastGameStatus.current === GameStatus.ENDED)
      ) {
        dispatch(gameActions.setCurrentTimer(+gameQuestions[0]?.newData?.data?.metadata?.timer));
      }
    }
    lastGameStatus.current = gameStatus;
  }, [gameQuestions, gameStatus, isRead]);

  //skip all speech recording questions in activity
  useEffect(() => {
    if (showPreQuiz) {
      dispatch(gameActions.setSkipSpeechRecordingQuestions(false));
    }
  }, [showPreQuiz]);

  const toSkipThisSpeechRecordingQuestion = () => {
    if (!showPreQuiz && currentQuestion && skipSpeechRecordingQuestions) {
      const isSpeechRecordingQuestion =
        currentQuestion.gameType === NewGameTypes.Speak ||
        (currentQuestion.gameType === NewGameTypes.NaturalConversation &&
          currentQuestion.newData?.data?.metadata?.skillId === NewGameTypes.Speak);

      if (isSpeechRecordingQuestion) {
        return true;
      }
    }

    return false;
  };

  useEffect(() => {
    const skip = async () => {
      dispatch(gameActions.toggleGameStatus(GameStatus.QUESTION_ANSWERED));
      dispatch(gameActions.setPickedAnswer({ answer: '' }));
      await toggleCheckAnswer('');
      goToNextQuestion();
    };

    if (toSkipThisSpeechRecordingQuestion()) {
      skip();
    }
  }, [currentQuestion, showPreQuiz, skipSpeechRecordingQuestions]);

  return {
    loadError,
    savingQuizProgressData,
    loadQuizProgress,
    currentQuestion,
    selectedType,
    selectedTopic,
    selectedSkill,
    selectedQuiz,
    gameStatus,
    timesUpShown,
    focusShown,
    correctAnswers,
    inCorrectAnswers,
    currentQuestionIndex,
    gameQuestions,
    gameTypeReal,
    coins,
    selectedHint,
    pickedAnswer,
    currentTime,
    shouldDisplayPopUp,
    popUpType,
    popUpProps,
    isMuted,
    currentCoins,
    allowRetry,
    showPreQuiz,
    showAnswerInOptions,
    onClosePopUp,
    onPause,
    onMute,
    onStartQuiz,
    onPractice,
    onExit,
    onDisableRetry,
    onAknowledge,
    onSwipe,
    goToNextQuestion,
    toggleCheckAnswer,
    currentReadQuestionIndex,
    currentQuestionTempData,
    setCurrentQuestionTempData,
  };
};

export default UseQuiz;
